/**
 * Shared utility functions for AI exchange feature validation
 */

import { ReturnTypeEnum } from "../../return";
import { Step } from "../../return-flow";
import { ShopifySubscription } from "../../shopify-subscription/shopify-subscription";
import {
  ExchangeAgentBillingSettings,
  ExchangeAgentMerchantSettings,
} from "../../team";
import { ReturnOptionMethod } from "../return-option";

/**
 * Checks if Exchange Agent is enabled based on both flow configuration and team settings
 *
 * @param flowStep - The flow step to check.
 * @param isExchangeAgentEnabledForTeam - Value that is calculated from ExchangeAgentBillingSettings and then passed as a boolean in ReturnSettings so we don't have to pass around the entire billing settings.
 */
export function isExchangeAgentEnabledForStep(
  returnType: ReturnTypeEnum | undefined,
  flowStep: Step.Return,
  isExchangeAgentEnabledForTeam: boolean,
): boolean {
  // Ensure this is a return and not a claim or warranty
  if (returnType !== ReturnTypeEnum.RETURN) {
    return false;
  }

  // Ensure this is a return step that allows exchange options
  if (!isExchangeAvailableForReturnStep(flowStep)) {
    return false;
  }

  return (
    isExchangeAgentEnabledForTeam &&
    // Validate that we didn't disable Exchange Agent for this particular step
    !flowStep?.exchangeAgentOverrides?.disableExchangeAgent
  );
}

interface BillableTeam {
  settings: { shopifySubscription?: ShopifySubscription[] | null };
}

export function isExchangeAgentEnabledForTeam(
  exchangeAgentBillingSettings: ExchangeAgentBillingSettings,
  exchangeAgentSettings: ExchangeAgentMerchantSettings,
  team: BillableTeam,
): boolean {
  // Does the merchant want to use Exchange Agent?
  if (!exchangeAgentSettings.enabled) {
    return false;
  }

  return isExchangeAgentAllowedForTeam(exchangeAgentBillingSettings, team);
}

/**
 * Does Redo allow the merchant to use Exchange Agent?
 *
 * You probably want to use {@link isExchangeAgentEnabledForTeam} instead.
 */
export function isExchangeAgentAllowedForTeam(
  exchangeAgentBillingSettings: ExchangeAgentBillingSettings,
  team: BillableTeam,
): boolean {
  // Does Redo allow the merchant to use Exchange Agent?
  if (!exchangeAgentBillingSettings.enabled) {
    return false;
  }

  // Do we expect to bill for Exchange Agent?
  if (!exchangeAgentBillingSettings.enableBilling) {
    // Allow Exchange Agent to be used for free.
    return true;
  }

  return true;

  /*
   ALEX NOTE:
   We were debating whether to lock people out if they haven't accepted billing in Shopify yet.
   But we are properly invoicing even if they haven't accepted billing in Shopify so might as well allow them to use Exchange Agent since we can still capture the revenue.
   */
  // We are trying to bill, so allow Exchange Agent if the team has accepted billing.
  // return isBillingAccepted(team);
}

// function isBillingAccepted(team: BillableTeam): boolean {
//   const shopifySubscription = team.settings.shopifySubscription?.[0];
//   const usagePlanId = getUsagePlanSubscriptionId(shopifySubscription);
//   return !!usagePlanId;
// }

function isExchangeAvailableForReturnStep(returnStep: Step.Return): boolean {
  const exchangeOptions = returnStep.options.filter(
    (option) => option.method.type === ReturnOptionMethod.EXCHANGE,
  );
  return exchangeOptions.some(
    (option) =>
      option.exchangeTypes.advanced ||
      option.exchangeTypes.variant ||
      option.exchangeTypes.sameItem ||
      option.exchangeTypes.exchangeGroups,
  );
}
